
import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue 
} from '@/components/ui/select';
import { PlusCircle, Trash2 } from 'lucide-react';

interface TeacherQualificationFieldsProps {
  formData?: any;
  handleChange?: (e: any) => void;
}

export const TeacherQualificationFields = ({ formData, handleChange }: TeacherQualificationFieldsProps) => {
  const qualifications = formData?.qualifications || [];

  const addQualification = () => {
    if (handleChange) {
      const newQualification = {
        id: crypto.randomUUID(),
        degree: '',
        field: '',
        institution: '',
        year: ''
      };

      handleChange({
        target: {
          name: 'qualifications',
          value: [...qualifications, newQualification]
        }
      });
    }
  };

  const removeQualification = (index: number) => {
    if (handleChange) {
      const updatedQualifications = [...qualifications];
      updatedQualifications.splice(index, 1);
      
      handleChange({
        target: {
          name: 'qualifications',
          value: updatedQualifications
        }
      });
    }
  };

  const updateQualification = (index: number, field: string, value: string) => {
    if (handleChange) {
      const updatedQualifications = [...qualifications];
      updatedQualifications[index] = {
        ...updatedQualifications[index],
        [field]: value
      };
      
      handleChange({
        target: {
          name: 'qualifications',
          value: updatedQualifications
        }
      });
    }
  };

  return (
    <div className="space-y-6">
      {qualifications.length === 0 ? (
        <div className="text-center p-8 border border-dashed rounded-lg">
          <p className="text-muted-foreground">No qualifications added yet</p>
        </div>
      ) : (
        qualifications.map((qual: any, index: number) => (
          <div key={qual.id || index} className="p-4 border rounded-lg">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-medium">Qualification {index + 1}</h3>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => removeQualification(index)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Remove
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor={`degree-${index}`}>Degree</Label>
                <Select 
                  value={qual.degree || ''} 
                  onValueChange={(value) => updateQualification(index, 'degree', value)}
                >
                  <SelectTrigger id={`degree-${index}`}>
                    <SelectValue placeholder="Select degree type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="highschool">High School</SelectItem>
                    <SelectItem value="associate">Associate Degree</SelectItem>
                    <SelectItem value="bachelor">Bachelor's Degree</SelectItem>
                    <SelectItem value="master">Master's Degree</SelectItem>
                    <SelectItem value="doctorate">Doctorate</SelectItem>
                    <SelectItem value="certificate">Professional Certificate</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor={`field-${index}`}>Field of Study</Label>
                <Input
                  id={`field-${index}`}
                  value={qual.field || ''}
                  onChange={(e) => updateQualification(index, 'field', e.target.value)}
                  placeholder="e.g. Computer Science"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor={`institution-${index}`}>Institution</Label>
                <Input
                  id={`institution-${index}`}
                  value={qual.institution || ''}
                  onChange={(e) => updateQualification(index, 'institution', e.target.value)}
                  placeholder="e.g. University of Example"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor={`year-${index}`}>Year Completed</Label>
                <Input
                  id={`year-${index}`}
                  value={qual.year || ''}
                  onChange={(e) => updateQualification(index, 'year', e.target.value)}
                  placeholder="e.g. 2020"
                />
              </div>
            </div>
          </div>
        ))
      )}
      
      <Button 
        type="button"
        variant="outline" 
        className="w-full"
        onClick={addQualification}
      >
        <PlusCircle className="h-4 w-4 mr-2" />
        Add Qualification
      </Button>
    </div>
  );
};
